import { create } from 'zustand'

/**
 * Zustand store for modals triggered from the sidebar.
 *
 * Problem: the sidebar can collapse (unmount its React tree) while a modal
 * is open. If the modal's open-state lives inside a sidebar component,
 * the state is lost and the modal disappears.
 *
 * Solution: keep modal open-state in a Zustand store that lives outside
 * the sidebar tree. A `<SidebarModalOutlet />` at the layout level
 * reads this store and renders the appropriate modal.
 *
 * URL-based modals (settings, tutorials, etc.) are NOT affected —
 * they use query-param state and should stay as-is.
 *
 * To add a new modal:
 * 1. Add a key to `SidebarModalKey`
 * 2. Register the component in `SIDEBAR_MODAL_REGISTRY` in sidebar-modal-outlet.tsx
 * 3. Call `useSidebarModalStore.getState().open('your-key')` from anywhere
 */

export type SidebarModalKey = 'credits-info' | 'credit-top-up'

interface ISidebarModalState {
    openModal: SidebarModalKey | null
    open: (key: SidebarModalKey) => void
    close: () => void
}

export const useSidebarModalStore = create<ISidebarModalState>((set) => ({
    openModal: null,
    open: (key) => set({ openModal: key }),
    close: () => set({ openModal: null }),
}))
